/**
 * 📋 Proyectos — visual por departamento con progress bars + SUB-TAREAS.
 * Cada proyecto tiene una lista de tareas. El % de progreso se calcula
 * automáticamente como (tareas hechas / total) × 100.
 *
 * - Click en el card → abre modal detalle con tareas
 * - Crear tarea → POST /api/v1/projects/:id/tasks
 * - Toggle tarea → PATCH /api/v1/project-tasks/:id { completed: true/false }
 * - Borrar tarea → DELETE /api/v1/project-tasks/:id
 * Backend recalcula progress_pct automáticamente tras cada cambio.
 */
import { h, apiClient, pill, emptyState, btn, openModal, openConfirm, toast, sectionHeader, openQuickTaskModal } from './_common.js';

const DEPTS = [
  { id: 'autosell',   label: 'Autosell',     color: '#E8A020', emoji: '🚗' },
  { id: 'inmuebles',  label: 'Inmuebles',    color: '#7CB342', emoji: '🏠' },
  { id: 'personal',   label: 'Personal',     color: '#5C6BC0', emoji: '🌟' },
  { id: 'carlos-ai',  label: 'CARLOS.AI',    color: '#AB47BC', emoji: '🤖' },
  { id: 'coco-doree', label: 'COCO/DORÉE',   color: '#EC407A', emoji: '🛍️' },
  { id: 'suiza-go',   label: 'Suiza-Go',     color: '#26C6DA', emoji: '🇨🇭' },
];

export async function renderProyectos(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' });
  wrap.appendChild(sectionHeader('📋', 'Proyectos', 'Crea proyecto → añádele tareas → marca check → el % se calcula solo', [
    btn('✅ + Tarea suelta', () => openQuickTaskModal('personal'), { variant: 'secondary' }),
    btn('+ Nuevo proyecto', () => openCreateProjectModal(() => renderProyectos(root))),
  ]));
  wrap.appendChild(h('div', { id: 'proj-list' }));
  root.appendChild(wrap);

  const list = wrap.querySelector('#proj-list');
  const res = await apiClient.get('/api/v1/projects').catch(() => ({ data: [] }));
  if (!res.data || res.data.length === 0) {
    list.appendChild(emptyState('📋', 'Sin proyectos todavía', 'Crea tu primer proyecto, añádele tareas y verás cómo se rellena el % a medida que las marcas.',
      btn('+ Crear proyecto', () => openCreateProjectModal(() => renderProyectos(root)))));
    return;
  }
  for (const d of DEPTS) {
    const items = res.data.filter((p) => p.department === d.id);
    if (items.length === 0) continue;
    const sec = h('section', { class: 'mb-6' }, [
      h('div', { class: 'flex items-center gap-2 mb-3' }, [
        h('span', { class: 'text-2xl' }, d.emoji),
        h('h3', { class: 'font-display font-semibold text-lg', style: `color: ${d.color}` }, d.label),
        h('span', { class: 'text-xs text-ink-400' }, `· ${items.length}`),
      ]),
      h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' },
        items.map((p) => projectCard(p, root))),
    ]);
    list.appendChild(sec);
  }
}

function projectCard(p, root) {
  const pct = p.progressPct || 0;
  return h('div', {
    class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 hover:border-gold-400/40 transition cursor-pointer group',
    onclick: () => openProjectDetail(p, () => renderProyectos(root)),
  }, [
    h('div', { class: 'flex items-start justify-between mb-2 gap-2' }, [
      h('h4', { class: 'font-semibold leading-tight flex-1' }, `${p.emoji || ''} ${p.name}`),
      pill(p.status, p.status === 'completado' ? 'green' : p.status === 'pausa' ? 'gray' : 'gold'),
    ]),
    p.description ? h('p', { class: 'text-sm text-ink-300 line-clamp-2 mb-2' }, p.description) : null,
    h('div', { class: 'h-2 bg-base-700 rounded-full overflow-hidden mt-3' }, [
      h('div', {
        class: `h-full transition-all ${pct === 100 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' : 'bg-gradient-to-r from-gold-400 to-gold-500'}`,
        style: `width: ${Math.max(2, pct)}%`,
      }),
    ]),
    h('div', { class: 'flex items-center justify-between mt-2' }, [
      h('span', { class: 'text-xs text-ink-300' }, `${pct}% completado`),
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { class: 'text-xs text-gold-300 group-hover:text-gold-200 transition' }, 'Abrir tareas →'),
        h('button', {
          class: 'text-xs text-red-400 opacity-0 group-hover:opacity-100 transition px-1',
          onclick: async (e) => {
            e.stopPropagation();
            const ok = await openConfirm({ title: 'Eliminar proyecto', message: `"${p.name}" (se borrarán también sus tareas)`, danger: true, icon: '🗑️' });
            if (!ok) return;
            await apiClient.delete(`/api/v1/projects/${p.id}`);
            toast('Proyecto eliminado', 'success');
            renderProyectos(root);
          },
        }, '🗑'),
      ]),
    ]),
  ]);
}

// ============================================================================
// MODAL DETALLE PROYECTO con sub-tareas
// ============================================================================
async function openProjectDetail(project, onDone) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) { overlay.remove(); onDone(); } },
  });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl' });

  // HEADER
  modal.appendChild(h('div', { class: 'p-5 border-b border-base-700 flex items-center justify-between sticky top-0 bg-base-900 z-10' }, [
    h('div', { class: 'flex items-center gap-3' }, [
      h('span', { class: 'text-3xl' }, project.emoji || '📋'),
      h('div', {}, [
        h('h3', { class: 'font-display font-bold text-xl' }, project.name),
        h('p', { class: 'text-xs text-ink-400' }, DEPTS.find((d) => d.id === project.department)?.label || project.department),
      ]),
    ]),
    h('div', { class: 'flex gap-2' }, [
      h('button', {
        class: 'text-xs text-ink-400 hover:text-gold-300 px-2 py-1',
        onclick: () => { overlay.remove(); openEditProjectModal(project, () => { onDone(); }); },
      }, '⚙ Editar'),
      h('button', { class: 'text-2xl text-ink-400 hover:text-ink-100 px-2', onclick: () => { overlay.remove(); onDone(); } }, '×'),
    ]),
  ]));

  // BODY (cargando)
  const body = h('div', { class: 'p-5' }, [
    h('div', { class: 'text-center text-ink-300 text-sm py-8' }, 'Cargando tareas…'),
  ]);
  modal.appendChild(body);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Render dinámico de las tareas
  async function refreshTasks() {
    try {
      const data = await apiClient.get(`/api/v1/projects/${project.id}/tasks`);
      body.innerHTML = '';

      // BARRA DE PROGRESO grande
      const pct = data.pct || 0;
      body.appendChild(h('div', { class: 'mb-5' }, [
        h('div', { class: 'flex items-center justify-between mb-2' }, [
          h('span', { class: 'text-sm font-semibold text-ink-200' }, `Progreso del proyecto`),
          h('span', { class: 'text-2xl font-display font-bold text-gold-300' }, `${pct}%`),
        ]),
        h('div', { class: 'h-3 bg-base-700 rounded-full overflow-hidden' }, [
          h('div', {
            class: `h-full transition-all duration-500 ${pct === 100 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' : 'bg-gradient-to-r from-gold-400 to-gold-500'}`,
            style: `width: ${Math.max(2, pct)}%`,
          }),
        ]),
        h('div', { class: 'text-xs text-ink-400 mt-1.5' }, `${data.done} de ${data.total} tareas completadas` + (data.total > 0 ? ` · cada tarea = ${Math.round(100 / data.total)}%` : '')),
      ]));

      // INPUT añadir nueva tarea (siempre arriba)
      const newTaskInput = h('input', {
        type: 'text',
        placeholder: '+ Añadir tarea y pulsa Enter…',
        class: 'w-full bg-base-800 border border-base-700 hover:border-gold-400/40 focus:border-gold-400 rounded-lg px-3 py-2.5 text-sm outline-none mb-3',
      });
      newTaskInput.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter') {
          const title = newTaskInput.value.trim();
          if (!title) return;
          newTaskInput.disabled = true;
          try {
            await apiClient.post(`/api/v1/projects/${project.id}/tasks`, { title });
            newTaskInput.value = '';
            await refreshTasks();
            newTaskInput.disabled = false;
            // Re-focus para que pueda añadir más sin click
            setTimeout(() => body.querySelector('input[type="text"]')?.focus(), 50);
          } catch (err) {
            toast('Error añadiendo tarea: ' + (err?.message || ''), 'error');
            newTaskInput.disabled = false;
          }
        }
      });
      body.appendChild(newTaskInput);

      // LISTA DE TAREAS
      if (data.data.length === 0) {
        body.appendChild(h('div', { class: 'text-center py-8 px-4 bg-base-800/30 border border-dashed border-base-700 rounded-lg' }, [
          h('div', { class: 'text-4xl mb-2' }, '📝'),
          h('p', { class: 'text-sm text-ink-300 mb-1' }, 'Sin tareas todavía'),
          h('p', { class: 'text-xs text-ink-400' }, 'Escribe arriba para añadir la primera. El % se llenará solo cuando las marques.'),
        ]));
      } else {
        const listEl = h('div', { class: 'space-y-1' });
        data.data.forEach((task) => listEl.appendChild(taskRow(task, refreshTasks)));
        body.appendChild(listEl);
      }
    } catch (err) {
      body.innerHTML = '';
      body.appendChild(h('p', { class: 'text-red-400 text-sm py-4' }, 'Error: ' + (err?.message || '')));
    }
  }

  await refreshTasks();
}

function taskRow(task, refresh) {
  const isDone = task.status === 'done';
  return h('div', {
    class: `flex items-center gap-3 px-3 py-2.5 rounded-lg border transition cursor-pointer group ${isDone ? 'bg-emerald-500/5 border-emerald-400/20' : 'bg-base-800/50 border-base-700 hover:border-gold-400/30'}`,
    onclick: async (e) => {
      if (e.target.closest('button')) return; // No togglear si click en botón borrar
      try {
        await apiClient.patch(`/api/v1/project-tasks/${task.id}`, { completed: !isDone });
        await refresh();
      } catch (err) {
        toast('Error: ' + (err?.message || ''), 'error');
      }
    },
  }, [
    // Checkbox custom
    h('div', {
      class: `w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${isDone ? 'bg-emerald-500 border-emerald-500' : 'border-base-600 group-hover:border-gold-400'}`,
    }, isDone ? h('span', { class: 'text-white text-xs font-bold' }, '✓') : null),
    // Título
    h('span', {
      class: `flex-1 text-sm ${isDone ? 'text-ink-400 line-through' : 'text-ink-100'}`,
    }, task.title),
    // Botón borrar
    h('button', {
      class: 'text-red-400 opacity-0 group-hover:opacity-100 transition text-xs px-2',
      onclick: async (e) => {
        e.stopPropagation();
        const ok = await openConfirm({ title: 'Eliminar tarea', message: `"${task.title}"`, danger: true });
        if (!ok) return;
        try {
          await apiClient.delete(`/api/v1/project-tasks/${task.id}`);
          await refresh();
        } catch (err) {
          toast('Error: ' + (err?.message || ''), 'error');
        }
      },
    }, '🗑'),
  ]);
}

// ============================================================================
// MODAL CREAR / EDITAR PROYECTO (sin % manual — se calcula con tareas)
// ============================================================================
function openCreateProjectModal(onDone) {
  openModal({
    title: 'Nuevo proyecto',
    icon: '📋',
    size: 'lg',
    fields: [
      { name: 'name', label: 'Nombre del proyecto', value: '', required: true, placeholder: 'p.ej. Lanzamiento Autosell v2' },
      { name: 'department', label: 'Departamento', type: 'select', value: 'personal', options: DEPTS.map((d) => ({ value: d.id, label: `${d.emoji} ${d.label}` })) },
      { name: 'emoji', label: 'Emoji', value: '🎯', placeholder: '🎯' },
      { name: 'status', label: 'Estado', type: 'select', value: 'activo', options: [
        { value: 'activo', label: 'Activo' },
        { value: 'pausa', label: 'En pausa' },
        { value: 'completado', label: 'Completado' },
        { value: 'cancelado', label: 'Cancelado' },
      ] },
      { name: 'description', label: 'Descripción (opcional)', type: 'textarea', rows: 3, value: '' },
    ],
    onSubmit: async (vals) => {
      const payload = {
        ...vals,
        slug: vals.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      };
      const r = await apiClient.post('/api/v1/projects', payload);
      toast('Proyecto creado. Ahora añádele tareas.', 'success');
      onDone();
      // Abrir el detalle del proyecto recién creado para añadir tareas
      setTimeout(() => {
        if (r?.data) openProjectDetail(r.data, onDone);
      }, 300);
    },
  });
}

function openEditProjectModal(project, onDone) {
  openModal({
    title: 'Editar proyecto',
    icon: '⚙',
    size: 'lg',
    fields: [
      { name: 'name', label: 'Nombre', value: project.name, required: true },
      { name: 'department', label: 'Departamento', type: 'select', value: project.department, options: DEPTS.map((d) => ({ value: d.id, label: `${d.emoji} ${d.label}` })) },
      { name: 'emoji', label: 'Emoji', value: project.emoji || '🎯' },
      { name: 'status', label: 'Estado', type: 'select', value: project.status, options: [
        { value: 'activo', label: 'Activo' },
        { value: 'pausa', label: 'En pausa' },
        { value: 'completado', label: 'Completado' },
        { value: 'cancelado', label: 'Cancelado' },
      ] },
      { name: 'description', label: 'Descripción', type: 'textarea', rows: 3, value: project.description || '' },
    ],
    onSubmit: async (vals) => {
      await apiClient.patch(`/api/v1/projects/${project.id}`, vals);
      toast('Proyecto actualizado', 'success');
      onDone();
    },
  });
}
